import * as THREE from 'three'
import { COLORS, FACES } from "../Global/materials";

// Convert degrees to radians
const toRad = Math.PI / 180;

// ViewCube class extending THREE.Object3D to represent a labeled 3D cube
export default class ViewCube extends THREE.Object3D {
    constructor({
        size = 60,
        outline = true,
        bgColor = COLORS.white,
        hoverColor = COLORS.blue,
        outlineColor = COLORS.black
    }) {
        super();
        this._cubeSize = size;
        this._outline = outline;
        this._bgColor = bgColor;
        this._hoverColor = hoverColor;
        this._outlineColor = outlineColor;
        this._build(); // Build the cube
    }

    // Constructs cube faces and outline
    _build() {
        const faceSize = this._cubeSize;
        const faceOffset = this._cubeSize / 2;

        // Create and label cube faces
        const cubeFaces = this._createCubeFaces(faceSize, faceOffset);
        for (let [i, props] of BOX_FACES.entries()) {
            cubeFaces.children[i].name = props.name;
            cubeFaces.children[i].material.color.setHex(this._bgColor);
            cubeFaces.children[i].material.map = props.map; // Assign label texture
        }
        this.add(cubeFaces);

        // Optionally add wireframe outline
        if (this._outline) {
            this.add(this._createCubeOutline(this._cubeSize));
        }
    }

    // Helper to create a single face with optional rotation and label
    _createFace(size, position, { axis = [0, 1, 0], angle = 0, visible = true, name = "", matProps = {} } = {}) {
        if (!Array.isArray(size)) size = [size, size]; // Ensure size is [width, height]
        const material = new THREE.MeshBasicMaterial({...matProps});
        const geometry = new THREE.PlaneGeometry(size[0], size[1]);
        const face = new THREE.Mesh(geometry, material);
        face.name = name;
        face.rotateOnAxis(new THREE.Vector3(...axis), angle * toRad); // Rotate face
        face.position.set(...position); // Position face in 3D space
        face.visible = visible;
        return face;
    }

    // Creates six faces of the cube in appropriate orientations
    _createCubeFaces(faceSize, offset) {
        const faces = new THREE.Object3D();
        faces.add(this._createFace(faceSize, [0, 0, offset], { axis: [0, 1, 0], angle: 0}));       // South
        faces.add(this._createFace(faceSize, [offset, 0, 0], { axis: [0, 1, 0], angle: 90 }));     // East
        faces.add(this._createFace(faceSize, [0, 0, -offset], { axis: [0, 1, 0], angle: 180 }));   // North
        faces.add(this._createFace(faceSize, [-offset, 0, 0], { axis: [0, 1, 0], angle: 270 }));   // West
        faces.add(this._createFace(faceSize, [0, offset, 0], { axis: [1, 0, 0], angle: -90, visible: false })); // Top (hidden)
        faces.add(this._createFace(faceSize, [0, offset, 0], { axis: [1, 0, 0], angle: -90 }));    // 3D (top label shown)
        return faces;
    }

    // Creates the wireframe outline of the cube
    _createCubeOutline(size) {
        const geometry = new THREE.BoxGeometry(size, size, size);
        const geo = new THREE.EdgesGeometry(geometry); // Extract edges
        const mat = new THREE.LineBasicMaterial({ color: this._outlineColor, linewidth: 2 });
        const wireframe = new THREE.LineSegments(geo, mat);
        return wireframe;
    }
}

// Constants for label rendering
const FONT_SIZE = 70;
const FONT = "Inter, sans-serif";

// Describes each face of the cube and assigns a label texture to it
const BOX_FACES = [
    {
        name: FACES.south,
        map: createTextSprite("S", { fontSize: FONT_SIZE, font: FONT })
    },
    {
        name: FACES.east,
        map: createTextSprite("E", { fontSize: FONT_SIZE, font: FONT })
    },
    {
        name: FACES.north,
        map: createTextSprite("N", { fontSize: FONT_SIZE, font: FONT, color: [255, 0, 0, 1.0] })
    },
    {
        name: FACES.west,
        map: createTextSprite("W", { fontSize: FONT_SIZE, font: FONT })
    },
    {
        name: FACES.top,
        map: createTextSprite("TOP", { fontSize: FONT_SIZE, font: FONT })
    },
    {
        name: FACES.threed,
        map: createTextSprite("3D", { fontSize: FONT_SIZE, font: FONT })
    },
];

// Creates a texture from canvas with text label
function createTextSprite(text, props) {
    const fontface = props.font || 'Helvetica';
    const fontsize = props.fontSize || 30;
    const width = props.width || 200;
    const height = props.height || 200;
    const bgColor = props.bgcolor ? props.bgColor.join(', ') : "255, 255, 255, 1.0";
    const fgColor = props.color ? props.color.join(', ') : "0, 0, 0, 1.0";
    
    // Create canvas and draw background
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.font = `bold ${fontsize}px ${fontface}`;
    context.fillStyle = `rgba(${bgColor})`;
    context.fillRect(0, 0, width, height);
    
    // Draw text in the center of the canvas
    const metrics = context.measureText(text);
    const textWidth = metrics.width;
    context.fillStyle = `rgba(${fgColor})`;
    context.fillText(text, width / 2 - textWidth / 2, height / 2 + fontsize / 2 - 2);
    
    // Create a THREE.js texture from the canvas
    const texture = new THREE.Texture(canvas)
    texture.minFilter = THREE.LinearFilter;
    texture.needsUpdate = true;
    return texture;
}
